import { useEffect, useState } from "react";
import posthog from "posthog-js";

import { supportedUSStatesForCompoundedMeds } from "~/shared/utils/sharedUtils";
import {
  getIsEditingPendingVisit,
  getUserID,
  getVisitID,
  getVisitUSState,
} from "~/visits/utils/visitCookies.js";

import {
  EXPERIMENT_PRODUCT_FLAVOR_PHOTOS,
  VARIANT_CONTROL,
  VARIANT_TEST,
} from "../experimentKeys";
import { useExperimentVariant } from "../useExperimentVariant";

export const FLAVOR_PHOTOS_EXPERIMENT = EXPERIMENT_PRODUCT_FLAVOR_PHOTOS;

// How long a selection screen waits for PostHog before it renders the control
// photos. posthog-js gives the flag request itself 3000 ms
// (feature_flag_request_timeout_ms), so this MUST be longer: settling first would
// render control while a slow-but-successful flag load records a test exposure,
// and the experiment would measure photos the patient never saw.
const FLAG_FALLBACK_MS = 4000;

// One decision per patient per checkout, kept for the life of the page. See
// useFlavorPhotosExperiment for why it is never revised once made.
const decisions = new Map();

// useFlavorPhotosExperiment decides whether the product / flavor selection rows show
// the reframed flat-lay product photos (ProductPhoto: 2:1 frame, zoomed, blended into
// the row) or the original renders in master's fixed 200×200 <img>. It is the only
// place that answers that question: the five selection screens read `reframed` and
// nothing else.
//
// ELIGIBILITY IS "THE PATIENT WILL SEE THE ROWS". Every selection screen redirects
// away when the checkout session, the visit id or a supported US state is missing,
// and an edit of an already submitted visit cannot produce a new Visit Checkout, so
// none of those patients can move the metric. They never ask for the flag, and so
// never record an exposure. A screen with its own extra guard (the sleep picker
// renders nothing unless the intake preference is gummies) passes `eligible`.
//
// EXPOSURE IS NATIVE. useExperimentVariant records $feature_flag_called when it
// resolves the flag; the experiment's funnel runs from that exposure through the
// server-sent Visit Checkout and Purchase events. Nothing here reports to our API.
// consultationType rides along as a session property so results can be read per
// vertical. posthog-js sends the exposure once per flag value per person, so a
// patient who visits select-flavored and then try-flavored is exposed on the first
// screen only; the screen itself is on the event as $pathname.
//
// THE DECISION IS FROZEN. Flags load asynchronously, and the same checkout walks
// through up to three of these screens. Once a checkout has rendered one photo set it
// must keep it: a late flag response that swapped the photos between screens, or
// under a half-made choice, would show the patient one experience and measure
// another. So the first settled value for a (user, checkout session) is kept in a
// module-level map, every later screen in the same checkout reads it back, and a
// flag answer that arrives after the fallback timer is ignored.
//
// Returns { ready, reframed }. Callers must not render either photo set until
// `ready`; `reframed` is true only for an eligible checkout frozen on the test
// variant. Holdout and unknown flag values already read as control upstream.
export function useFlavorPhotosExperiment({
  checkoutSessionID,
  consultationType,
  eligible = true,
}) {
  const userID = getUserID();
  const active = Boolean(
    eligible &&
    userID &&
    checkoutSessionID &&
    getVisitID(checkoutSessionID) &&
    !getIsEditingPendingVisit(checkoutSessionID) &&
    supportedUSStatesForCompoundedMeds.includes(getVisitUSState()),
  );
  const key = `${userID}:${checkoutSessionID}`;

  // Flags are evaluated per person, and the server-sent funnel events carry the
  // user id as distinct_id, so the exposure has to be recorded against the same
  // identity or the funnel cannot join exposure to checkout.
  useEffect(() => {
    if (active) posthog.identify(userID);
  }, [active, userID]);

  const { ready: flagsReady, variant } = useExperimentVariant(
    FLAVOR_PHOTOS_EXPERIMENT,
    {
      eligible: active,
      exposureProperties: active
        ? { consultation_type: consultationType }
        : undefined,
    },
  );

  const [decision, setDecision] = useState(() => decisions.get(key));

  useEffect(() => {
    if (!active) return undefined;
    if (decisions.has(key)) {
      setDecision(decisions.get(key));
      return undefined;
    }

    let canceled = false;
    const settle = (settledVariant) => {
      if (canceled || decisions.has(key)) return;
      const frozen = { key, variant: settledVariant };
      decisions.set(key, frozen);
      setDecision(frozen);
    };

    if (flagsReady) {
      settle(variant);
      return undefined;
    }

    // Photos are presentation; a slow flag service must not hold up the checkout.
    const timer = setTimeout(() => settle(VARIANT_CONTROL), FLAG_FALLBACK_MS);
    return () => {
      canceled = true;
      clearTimeout(timer);
    };
  }, [active, key, flagsReady, variant]);

  const frozen = decision?.key === key ? decision : undefined;
  return {
    ready: !active || Boolean(frozen),
    reframed: Boolean(active && frozen?.variant === VARIANT_TEST),
  };
}
